import { useMutation, useQueryClient } from '@tanstack/react-query';

import { NIL_UUID } from '../constants';
import { IErrorNotif, IErrorNotifUpsert } from '../types';
import useImperativeRequestWrapper from '@/hooks/useImperativeRequestWrapper';
import { MutationConfig } from '@/lib/react-query';

type ImperativeRequestFn = ReturnType<typeof useImperativeRequestWrapper>[1];

interface IUpsertErrorNotifArgs {
  errNotifId?: string;
  values: IErrorNotifUpsert;
}

const upsertErrorNotif =
  ({
    partnerId,
    makeRequest,
  }: {
    partnerId: string;
    makeRequest: ImperativeRequestFn;
  }) =>
  async ({
    errNotifId,
    values,
  }: IUpsertErrorNotifArgs): Promise<IErrorNotif> => {
    const { data, error } = await makeRequest({
      path: `/partners/${partnerId}/webhooks/error-notifs/${errNotifId ?? NIL_UUID}`,
      method: 'post',
      body: values,
    });
    if (error || !data) throw new Error(error?.message ?? 'Upsert failed');

    return data;
  };

type UseUpsertErrorNotifOptions = {
  partnerId: string;
  config?: MutationConfig<ReturnType<typeof upsertErrorNotif>>;
};

export const useUpsertErrorNotif = ({
  partnerId,
  config,
}: UseUpsertErrorNotifOptions) => {
  const [, makeRequest] = useImperativeRequestWrapper('lpVillageV3');
  const queryClient = useQueryClient();

  return useMutation({
    ...config,
    mutationFn: upsertErrorNotif({ partnerId, makeRequest }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['webhook-error-notifs', partnerId],
      });
    },
  });
};
